import { useEffect, useState } from 'react';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent, AccordionTitle } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

import { StatusBadge, formatDate, formatPercent } from './TrilhaStudentsShared';

const BLOCK_STATE = {
  done: { label: 'Concluído', variant: 'success' },
  skipped: { label: 'Pulado', variant: 'warning' },
  pending: { label: 'Pendente', variant: 'neutral-lightest' }
};

const BLOCK_KIND = {
  content: 'Conteúdo',
  review: 'Revisão'
};

function BlockSummary({ block, heading }) {
  const state = BLOCK_STATE[block.state] || BLOCK_STATE.pending;
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-start justify-between gap-2">
        {heading ? (
          <span className="text-xs font-bold uppercase tracking-wide text-primary-dark">{heading}</span>
        ) : (
          <span className="min-w-0 break-words text-sm font-medium text-primary-dark">{block.video_title || block.taxonomy_name || BLOCK_KIND[block.kind]}</span>
        )}
        <Badge
          variant={state.variant}
          className="shrink-0"
        >
          {state.label}
        </Badge>
      </div>
      {heading && <span className="mt-2 block break-words text-sm font-medium text-primary-dark">{block.video_title || block.taxonomy_name || BLOCK_KIND[block.kind]}</span>}
      <span className="mt-1 block text-xs text-neutral">
        {BLOCK_KIND[block.kind] || block.kind}
        {block.completed_at ? ` · concluído em ${formatDate(block.completed_at)}` : ''}
      </span>
      {block.taxonomy_name && (
        <Badge
          variant="muted"
          size="xs"
          className="mt-1 whitespace-normal font-bold text-neutral-dark"
        >
          {block.taxonomy_name}
        </Badge>
      )}
    </div>
  );
}

function BlockList({ blocks }) {
  if (!blocks.length) return <p className="py-4 text-center text-sm text-neutral">Sem blocos para exibir.</p>;
  return (
    <ul className="space-y-2">
      {blocks.map(block => (
        <li
          key={block.position}
          className="flex items-start gap-3 rounded-lg border border-neutral-lightest bg-white p-3"
        >
          <span className="w-8 shrink-0 text-center font-mono text-xs text-neutral">{block.position + 1}</span>
          <BlockSummary block={block} />
        </li>
      ))}
    </ul>
  );
}

// `student.id` é o UserLearningPath da linha; o agrupamento vem da listagem.
export default function TrilhaProgressModal({ open, onOpenChange, student }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const userLearningPathId = student?.id;
  const grouping = student?.grouping;

  useEffect(() => {
    if (!open || !userLearningPathId) {
      setDetail(null);
      return;
    }
    let active = true;
    setDetail(null);
    setError(false);
    setLoading(true);
    adminLearningPathsApi
      .studentDetail(userLearningPathId, grouping)
      .then(json => {
        if (!active) return;
        const attributes = json?.data?.attributes;
        if (attributes) setDetail(attributes);
        else setError(true);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, userLearningPathId, grouping]);

  const renderContent = () => {
    if (loading) return <p className="py-4 text-center text-sm text-neutral">Carregando progresso...</p>;
    if (error) return <p className="py-4 text-center text-sm text-neutral-dark">Não foi possível carregar o progresso do aluno.</p>;
    if (detail?.chapters) {
      if (!detail.chapters.length) return <p className="py-4 text-center text-sm text-neutral">Sem capítulos para exibir.</p>;
      return (
        <Accordion
          type="multiple"
          className="min-w-0"
        >
          {detail.chapters.map(chapter => (
            <AccordionItem
              key={chapter.id}
              value={String(chapter.id)}
              className="min-w-0 overflow-hidden data-[state=open]:border-neutral-300"
            >
              <AccordionTrigger className="data-[state=open]:bg-neutral-50">
                <AccordionTitle className="text-primary-dark">Capítulo {chapter.chapter}</AccordionTitle>
                <StatusBadge status={chapter.status} />
              </AccordionTrigger>
              <AccordionContent>
                <BlockList blocks={chapter.blocks || []} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      );
    }
    const blocks = detail?.blocks || [];
    if (grouping !== 'section' || !blocks.length) return <BlockList blocks={blocks} />;
    return (
      <Accordion
        type="multiple"
        className="min-w-0"
      >
        {blocks.map(block => (
          <AccordionItem
            key={block.position}
            value={String(block.position)}
            className="min-w-0 overflow-hidden data-[state=open]:border-neutral-300"
          >
            <AccordionTrigger className="items-start data-[state=open]:bg-neutral-50">
              <BlockSummary
                block={block}
                heading={`Bloco ${block.position + 1}`}
              />
            </AccordionTrigger>
            <AccordionContent className="border-t-0">
              <p className="mb-3 text-xs font-bold uppercase tracking-wide text-neutral">Seções do bloco</p>
              {block.sections?.length ? (
                <ul className="space-y-2">
                  {[...block.sections]
                    .sort((a, b) => a.position - b.position)
                    .map(section => (
                      <li
                        key={`${section.position}-${section.scorm_id}`}
                        className="flex items-start gap-3 rounded-lg border border-neutral-lightest bg-white p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="mb-0 break-words text-sm font-medium text-primary-dark">{section.title || `Seção ${section.position}`}</p>
                          {section.done_at && <p className="mb-0 text-xs text-neutral">Concluído em {formatDate(section.done_at)}</p>}
                        </div>
                        <Badge
                          variant={section.done ? 'success' : 'neutral-lightest'}
                          className="shrink-0"
                          size="sm"
                        >
                          {section.done ? 'Concluído' : 'Pendente'}
                        </Badge>
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="py-4 text-center text-sm text-neutral">Sem seções para exibir.</p>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        size="lg"
        className="overflow-x-hidden"
      >
        <DialogHeader>
          <DialogTitle>{student?.user_name || detail?.user_name}</DialogTitle>
          <DialogDescription>
            Progresso na trilha {student?.title || detail?.learning_path_name || ''} — {formatPercent(student?.percent_walked)} concluído, {formatPercent(student?.percent_mastered)} dominado.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
}
